<template>
  <div class="custom-dropdown-container">
    <div class="custom-select pointer" :class="{ disabled }">
      <div class="selector" ref="dropdownParent">
        <div class="select-label show-error-bdr" @click="toggle">
          <div class="elipsis-text mr-4" v-overflow-tooltip>
            {{ triggerLabel }}
          </div>
        </div>
        <svg class="dropdown-icon" @click.stop="toggle" :class="{ rotated: visible }" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div
          class="dropdown"
          :class="[
            { hidden: !visible, visible },
            openUp ? 'dropdown-up' : 'dropdown-down'
          ]"
          :style="dropdownStyle"
        >
          <div class="dropdown-content" :class="{ upward: openUp }" @mousemove="disableMouseOver = false">
            <div v-if="!openUp" class="slot-container">
              <div class="search-box">
                <input
                  ref="searchInput"
                  v-model.trim="searchText"
                  type="text"
                  class="search-input"
                  placeholder="Search users"
                  @click.stop
                >
              </div>
            </div>

            <ul ref="dropdownList">
              <li
                :class="{
                  'v-center': true,
                  'highlight-item': highlightedIndex === 0,
                }"
                @click.stop="toggleSelectAll"
                @mouseover="highlightedIndex = disableMouseOver ? highlightedIndex : 0"
              >
                <div class="option-check">
                  <span class="checkmark" :class="{ checked: allFilteredSelected }"></span>
                </div>
                <div class="w-100">
                  <div class="elipsis-text list-name">Select all</div>
                  <div class="xs list-subtext">{{ filteredOptions.length }} users</div>
                </div>
              </li>
              <div class="notMatching" v-if="filteredOptions.length === 0">No users found</div>
              <li
                v-for="(item, index) in filteredOptions"
                v-else
                :key="item.value"
                :ref="`option-${item.value}`"
                :class="{
                  'v-center': true,
                  'highlight-item': highlightedIndex === index + 1
                }"
                @click.stop="toggleOption(item)"
                @mouseover="highlightedIndex = disableMouseOver ? highlightedIndex : index + 1"
              >
                <div class="option-check">
                  <span class="checkmark" :class="{ checked: isSelected(item.value) }"></span>
                </div>
                <div class="w-100">
                  <div class="elipsis-text list-name" v-overflow-tooltip>{{ item.label }}</div>
                  <div v-if="item.subLabel" class="xs elipsis-text list-subtext" v-overflow-tooltip>{{ item.subLabel }}</div>
                </div>
              </li>
            </ul>

            <div v-if="openUp" class="slot-container">
              <div class="search-box">
                <input
                  ref="searchInputBottom"
                  v-model.trim="searchText"
                  type="text"
                  class="search-input"
                  placeholder="Search users"
                  @click.stop
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserMultiSelectDropdown',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    initiallySelected: {
      type: Array,
      default: () => []
    },
    defaultLabel: {
      type: String,
      default: 'Select users'
    },
    defaultStyle: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: false,
      openUp: false,
      dropdownStyle: {},
      highlightedIndex: 0,
      disableMouseOver: false,
      selectedValues: [],
      searchText: '',
      repositionFrame: null,
      resizeObserver: null
    }
  },
  computed: {
    filteredOptions () {
      if (!this.searchText) return this.options
      const search = this.searchText.toLowerCase()
      return this.options.filter(item => {
        return item.label?.toLowerCase().includes(search) || item.subLabel?.toLowerCase().includes(search)
      })
    },
    allFilteredSelected () {
      return this.filteredOptions.length > 0 && this.filteredOptions.every(item => this.selectedValues.includes(item.value))
    },
    highlightedItem () {
      if (this.highlightedIndex === 0) return { type: 'all' }
      return this.filteredOptions[this.highlightedIndex - 1] || null
    },
    triggerLabel () {
      if (!this.selectedValues.length) return this.defaultLabel
      if (this.selectedValues.length === 1) {
        return this.options.find(item => item.value === this.selectedValues[0])?.label || this.defaultLabel
      }
      return `${this.selectedValues.length} users selected`
    }
  },
  methods: {
    getSearchInput () {
      return this.$refs.searchInput || this.$refs.searchInputBottom || null
    },
    focusSearchInput ({ placeCursorAtEnd = false } = {}) {
      this.$nextTick(() => {
        const input = this.getSearchInput()
        if (!input || typeof input.focus !== 'function') return
        input.focus()
        if (placeCursorAtEnd && typeof input.setSelectionRange === 'function') {
          const valueLength = input.value?.length || 0
          input.setSelectionRange(valueLength, valueLength)
        }
      })
    },
    updateSearchInputValue (input, value) {
      input.value = value
      input.dispatchEvent(new Event('input', { bubbles: true }))
    },
    shouldTypeIntoSearch (e) {
      if (e.ctrlKey || e.metaKey || e.altKey) return false
      if (e.key.length === 1) return true
      return e.key === 'Backspace'
    },
    toggle (e) {
      if (this.disabled) return
      this.visible = !this.visible
      if (this.visible) {
        this.highlightedIndex = this.filteredOptions.length ? 1 : 0
        this.addKeyListener()
        this.updateDropdownPosition()
        this.focusSearchInput()
      } else {
        this.closeDropdown()
      }
    },
    closeDropdown () {
      this.visible = false
      this.dropdownStyle = {}
      this.removeKeyListener()
    },
    emitSelected () {
      this.$emit('selected', this.options.filter(item => this.selectedValues.includes(item.value)))
    },
    isSelected (value) {
      return this.selectedValues.includes(value)
    },
    toggleOption (item) {
      if (this.isSelected(item.value)) {
        this.selectedValues = this.selectedValues.filter(value => value !== item.value)
      } else {
        this.selectedValues = [...this.selectedValues, item.value]
      }
      this.emitSelected()
      this.scheduleDropdownPositionUpdate()
    },
    toggleSelectAll () {
      if (!this.filteredOptions.length) return
      const filteredValues = this.filteredOptions.map(item => item.value)
      if (this.allFilteredSelected) {
        this.selectedValues = this.selectedValues.filter(value => !filteredValues.includes(value))
      } else {
        this.selectedValues = [...new Set([...this.selectedValues, ...filteredValues])]
      }
      this.emitSelected()
      this.scheduleDropdownPositionUpdate()
    },
    scheduleDropdownPositionUpdate () {
      if (!this.visible) return
      if (this.repositionFrame) {
        window.cancelAnimationFrame(this.repositionFrame)
      }
      this.repositionFrame = window.requestAnimationFrame(() => {
        this.repositionFrame = window.requestAnimationFrame(() => {
          this.updateDropdownPosition()
        })
      })
    },
    updateDropdownPosition () {
      const trigger = this.$el?.querySelector('.select-label')
      if (!trigger) return
      const triggerRect = trigger.getBoundingClientRect()
      this.checkDropdownDirection(triggerRect.width, triggerRect.bottom, triggerRect.left)
    },
    checkDropdownDirection (widthPassed, parentBottom, parentLeft) {
      this.$nextTick(() => {
        const dropdown = this.$el.querySelector('.dropdown')
        const trigger = this.$el.querySelector('.select-label')
        if (!dropdown || !trigger) return
        const triggerRect = trigger.getBoundingClientRect()
        const dropdownHeight = dropdown.offsetHeight
        const spaceBelow = window.innerHeight - triggerRect.bottom
        const spaceAbove = triggerRect.top
        if (dropdownHeight > spaceBelow && spaceAbove > dropdownHeight) {
          this.openUp = true
          const bottomSpace = window.innerHeight - triggerRect.top
          this.dropdownStyle = {
            position: 'fixed',
            width: `${triggerRect.width}px`,
            zIndex: 9999,
            bottom: `${bottomSpace}px`,
            left: `${parentLeft}px`,
            overflow: 'visible',
            border: '1px solid gainsboro'
          }
        } else {
          this.openUp = false
          this.dropdownStyle = {
            position: 'fixed',
            top: `${parentBottom}px`,
            left: `${parentLeft}px`,
            width: `${widthPassed}px`,
            zIndex: 999
          }
        }
      })
    },
    addKeyListener () {
      window.addEventListener('keydown', this.handleKeyDown)
    },
    removeKeyListener () {
      window.removeEventListener('keydown', this.handleKeyDown)
    },
    handleViewportChange () {
      this.scheduleDropdownPositionUpdate()
    },
    scrollToHighlightedItem () {
      this.$nextTick(() => {
        if (this.highlightedIndex === 0) {
          const list = this.$refs.dropdownList
          if (list?.scrollTo) list.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
        const item = this.highlightedItem
        const itemEl = this.$refs[`option-${item?.value}`]?.[0]
        if (itemEl?.scrollIntoView) {
          itemEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },
    handleKeyDown (e) {
      if (!this.visible) return
      const activeElement = document.activeElement
      const isEditableTarget = activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable)
      const searchInput = this.getSearchInput()

      if (!isEditableTarget && searchInput && this.shouldTypeIntoSearch(e)) {
        e.preventDefault()
        this.focusSearchInput({ placeCursorAtEnd: true })
        if (e.key === 'Backspace') {
          this.updateSearchInputValue(searchInput, searchInput.value.slice(0, -1))
        } else {
          this.updateSearchInputValue(searchInput, `${searchInput.value}${e.key}`)
        }
        return
      }

      if (isEditableTarget) {
        if (e.key === 'Escape') {
          e.preventDefault()
          this.closeDropdown()
        }
        return
      }

      const maxIndex = this.filteredOptions.length
      switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        this.disableMouseOver = true
        this.highlightedIndex = Math.max(0, this.highlightedIndex - 1)
        this.scrollToHighlightedItem()
        break
      case 'ArrowDown':
        e.preventDefault()
        this.disableMouseOver = true
        this.highlightedIndex = Math.min(maxIndex, this.highlightedIndex + 1)
        this.scrollToHighlightedItem()
        break
      case 'Enter':
        e.preventDefault()
        if (this.highlightedIndex === 0) {
          this.toggleSelectAll()
        } else if (this.highlightedItem) {
          this.toggleOption(this.highlightedItem)
        }
        break
      case 'Escape':
        e.preventDefault()
        this.closeDropdown()
        break
      }
    },
    closeOnOutsideClick (e) {
      if (this.visible && !this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    },
    defaultStyleUpdate () {
      if (!this.$el || !this.defaultStyle) return
      if (this.defaultStyle.dropDownWidth) {
        this.$el.style.setProperty('--dropDownWidth', this.defaultStyle.dropDownWidth)
      }
      if (this.defaultStyle.borderColor) {
        this.$el.style.setProperty('--borderColor', this.defaultStyle.borderColor)
      }
      if (this.defaultStyle.backGroundColor) {
        this.$el.style.setProperty('--backGroundColor', this.defaultStyle.backGroundColor)
      }
      if (this.defaultStyle.labelHeight) {
        this.$el.style.setProperty('--labelHeight', this.defaultStyle.labelHeight)
      }
      if (this.defaultStyle.ddLabelFontSize) {
        this.$el.style.setProperty('--ddLabelFontSize', this.defaultStyle.ddLabelFontSize)
      }
      if (this.defaultStyle.ddMaxWidth) {
        this.$el.style.setProperty('--ddMaxWidth', this.defaultStyle.ddMaxWidth)
      }
    }
  },
  watch: {
    searchText () {
      this.highlightedIndex = this.filteredOptions.length ? 1 : 0
    },
    initiallySelected: {
      handler (newVal) {
        this.selectedValues = [...newVal]
      },
      deep: true,
      immediate: true
    },
    defaultStyle: {
      handler () {
        this.defaultStyleUpdate()
      },
      deep: true
    }
  },
  mounted () {
    window.document.addEventListener('click', this.closeOnOutsideClick)
    window.addEventListener('resize', this.handleViewportChange)
    window.addEventListener('scroll', this.handleViewportChange, true)
    if (typeof window.ResizeObserver === 'function') {
      this.resizeObserver = new window.ResizeObserver(() => {
        this.scheduleDropdownPositionUpdate()
      })
      this.resizeObserver.observe(this.$el)
      if (this.$el.parentElement) {
        this.resizeObserver.observe(this.$el.parentElement)
      }
    }
    this.defaultStyleUpdate()
  },
  beforeDestroy () {
    window.document.removeEventListener('click', this.closeOnOutsideClick)
    window.removeEventListener('resize', this.handleViewportChange)
    window.removeEventListener('scroll', this.handleViewportChange, true)
    if (this.repositionFrame) {
      window.cancelAnimationFrame(this.repositionFrame)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.removeKeyListener()
  }
}
</script>

<style lang="scss" scoped>
:root {
  --borderColor: #d1d5db;
  --dropDownWidth: 100%;
  --backGroundColor: #fff;
  --labelHeight: 34.2px;
  --ddLabelFontSize: 1em;
  --ddMaxWidth: 100%;
}

.custom-dropdown-container {
  width: var(--dropDownWidth);
  max-width: var(--ddMaxWidth);
}

.custom-select {
  width: var(--dropDownWidth);

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}

.selector {
  position: relative;
}

.select-label {
  display: flex;
  align-items: center;
  height: var(--labelHeight);
  padding: 0 36px 0 8px;
  border-bottom: 1px solid var(--borderColor, #d1d5db);
  background: var(--backGroundColor);
  font-size: var(--ddLabelFontSize);
  &:last-child {
    border-bottom: none;
  }

}

.dropdown {
  position: fixed;
  z-index: 999;
  background: #fff;
  border: 1px solid rgb(243, 241, 241);
  overflow: hidden;
  max-height: 20rem;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.1) 0 4px 6px -1px, rgba(0, 0, 0, 0.06) 0 2px 4px -1px;
}

.dropdown-content {
  display: flex;
  flex-direction: column;
  max-height: 20rem;
  overflow: hidden;
}

.slot-container {
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.search-box {
  padding: 8px;
}

.search-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  outline: none;

  &:focus {
    border-color: var(--brand-color);
  }
}

ul {
  overflow: auto;
  flex: 1;
  margin: 0;
  padding: 8px;
  list-style: none;
  font-size: var(--text-xs2);
  font-weight: 400;
}

li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  color: #000;
  border-radius: 6px;
}

.current {
  background-color: transparent;
}

.highlight-item {
  background-color: transparent;
}

.option-check {
  padding-top: 2px;
}

.checkmark {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 1.5px solid var(--brand-color);
  border-radius: 4px;
  background: #fff;
  position: relative;

  &.checked::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid var(--brand-color);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
}

.list-name {
  display: flex;
  align-items: center;
}

.list-subtext {
  color: #6b7280;
  margin-top: 2px;
}

.hidden {
  visibility: hidden;
}

.visible {
  visibility: visible;
}

.notMatching {
  color: #888;
  font-size: 0.85em;
  padding: 8px;
  text-align: center;
}

.dropdown-icon {
  width: 15px;
  height: 15px;
  color: #6b7280;
  transition: transform 0.3s ease;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.dropdown-icon.rotated {
  transform: rotate(180deg) translateY(50%);
}
</style>
